import React, { useEffect, useState, useContext } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { StripeContext } from '../../context/StripeContext';
import { createCheckoutSession } from '../../utils/api';
import Dialog from '@mui/material/Dialog';
import { DialogContent } from '@mui/material';
import CloseIcon from '../../svg/CloseIcon';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import LoadingPage from '../LoadingPage';
import { logging } from '../../utils/logger';
import { useNavigate } from 'react-router-dom';

interface StripeCheckoutFormProps {
  item: string,
  isOpen: boolean,
  setIsOpen: any,
  itemId?: string,
  redirectURL?: string,
  isPremium?: boolean,
  trialPeriod?: number,
}

const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = (props) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [referral, setReferral] = useState<string>("");
  const [StripeComponents, setStripeComponents] = useState<any>(null);
  const { authUser, authLoading, setLoginModalVisible } = useAuth();
  const { stripePromise, isIframeEnvironment } = useContext(StripeContext);
  const { item, itemId, isOpen=false, setIsOpen, redirectURL, isPremium=false, trialPeriod=0 } = props;

  const navigate = useNavigate()

  // Dynamically import Stripe components only if not in iframe
  useEffect(() => {
    if (!isIframeEnvironment && !StripeComponents) {
      import('@stripe/react-stripe-js').then((module) => {
        setStripeComponents({
          EmbeddedCheckout: module.EmbeddedCheckout,
          EmbeddedCheckoutProvider: module.EmbeddedCheckoutProvider,
        });
      });
    }
  }, [isIframeEnvironment, StripeComponents]);

  useEffect(() => {
    // @ts-ignore
    setReferral(window.Rewardful?.referral || '')
  }, []);

  useEffect(() => {
    // Don't proceed if in iframe environment
    if (isIframeEnvironment) {
      return;
    }

    if (item && isOpen && authUser &&
      authUser.documentId && (authUser.email || authUser.customerId)
    ){
      createCheckoutSession(
        authUser?.documentId,
        authUser?.email,
        item,
        authUser?.customerId,
        itemId,
        redirectURL,
        isPremium,
        trialPeriod,
        referral
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.isFree) {
            if (item == "free_course") {
              navigate(`/learn/${itemId}`)
            }
            else {
              handleClose();
              window.location.reload();
            }
          }
          else {
            setClientSecret(data.clientSecret)
          }
          // setClientSecret(data.clientSecret)
        })
        .catch((err) => {
          logging.error('Error in Stripe Checkout Form', {context: {
            err, item, itemId, isPremium, trialPeriod, referral,
            customerId: authUser?.customerId,
            userId: authUser?.documentId,
            email:  authUser?.email,
          }})
          handleClose();
        })
    } else if (item && isOpen && !authLoading) {
      // if trying to open but user is not logged in, force user to login
      setLoginModalVisible({
        isOpen: true,
        formType: 'signin',
        currentStep: 1,
      });
    }
  }, [item, referral, authUser, authLoading, isIframeEnvironment]) //eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setClientSecret(null)
    setIsOpen(false);
  };

  // Don't render anything if in iframe environment
  if (isIframeEnvironment) {
    return null;
  }

  const options = {
    clientSecret,
  } as const

  const renderStripeCheckout = () => {
    if (!StripeComponents || !clientSecret || !stripePromise) {
      return (
        <Box sx={{display: 'flex', justifyContent: 'center'}}>
          <LoadingPage />
        </Box>
      );
    }

    const { EmbeddedCheckout, EmbeddedCheckoutProvider } = StripeComponents;

    return (
      <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      fullWidth={true}
      maxWidth={'lg'}
      slotProps={{
        paper: {
          sx: {
            background: 'white',
          }
        }
      }}
    >
      <DialogContent>
        <Box>
          <IconButton
            id='button-close-stripe-checkout'
            onClick={handleClose}
            color='primary'
            sx={{
              position: 'absolute',
              alignSelf: 'flex-end',
              minWidth: '36px',
              minHeight: '36px',
              height: '36px',
              width: '36px',
              top: '16px',
              right: '16px',
            }}
          >
            <CloseIcon height='30px' width='30px' />
          </IconButton>
          {renderStripeCheckout()}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default StripeCheckoutForm;
